import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import LogoutIcon from "@mui/icons-material/Logout";
import PeopleIcon from "@mui/icons-material/People";
import AssignmentIcon from "@mui/icons-material/Assignment";
import InventoryIcon from "@mui/icons-material/Inventory";
import PaymentIcon from "@mui/icons-material/Payments";
import StoreIcon from "@mui/icons-material/Store";
import DescriptionIcon from "@mui/icons-material/Description";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import VerifiedIcon from "@mui/icons-material/Verified";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import Tooltip from "@mui/material/Tooltip";
import Switch from "@mui/material/Switch";
import Avatar from "@mui/material/Avatar";
import axios from "axios";
import "../styles/Sidebar.css";
import logo from "../resources/blanco.png";
import NotificacionesReportes from "../Modules/Reportes/Components/NotificacionesReportes";

const ROLES = {
  ADMIN: 1,
  FINANCIAL: 2,
  SALES: 3,
  WAREHOUSE: 4,
  AUXILIAR: 5
};

const ROLE_ICONS = {
  [ROLES.ADMIN]: <AdminPanelSettingsIcon className="role-icon" />,
  [ROLES.FINANCIAL]: <PaymentIcon className="role-icon" />,
  [ROLES.SALES]: <AssignmentIcon className="role-icon" />,
  [ROLES.WAREHOUSE]: <StoreIcon className="role-icon" />,
  [ROLES.AUXILIAR]: <InventoryIcon className="role-icon" />
};

const ROLE_LABELS = {
  [ROLES.ADMIN]: "ADMINISTRADOR",
  [ROLES.FINANCIAL]: "FINANCIERO",
  [ROLES.SALES]: "VENTAS",
  [ROLES.WAREHOUSE]: "LOGÍSTICO",
  [ROLES.AUXILIAR]: "AUXILIAR"
};

function Sidebar({ children }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem("soft-dark-mode") === "true");
  const [hoveredModule, setHoveredModule] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  const [userName, setUserName] = useState("");
  const [userPicture, setUserPicture] = useState(() => localStorage.getItem("userPicture") || "");
  const [reportesNoLeidas, setReportesNoLeidas] = useState(0);
  // null significa que todavía no se ha podido comprobar la asociación.
  const [tieneIdAleluya, setTieneIdAleluya] = useState(null);
  const sidebarRef = useRef(null);
  const menuButtonRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    document.documentElement.classList.toggle("theme-soft-dark", isDarkMode);
    localStorage.setItem("soft-dark-mode", String(isDarkMode));

    return () => document.documentElement.classList.remove("theme-soft-dark");
  }, [isDarkMode]);

  const handleThemeChange = (event) => {
    setIsDarkMode(event.target.checked);
  };

  // Obtener el rol y nombre del usuario desde localStorage
  useEffect(() => {
    const role = localStorage.getItem("userRole");
    const name = localStorage.getItem("userName");
    const idUsuario = localStorage.getItem("userId");
    
    if (role) {
      const roleNumber = parseInt(role);
      setUserRole(roleNumber);
    }
    
    if (name) {
      setUserName(name.toUpperCase());
    } else {
      setUserName("INDEFINIDO");
    }

    try {
      const storedPermissions = localStorage.getItem("userPermissions");
      if (storedPermissions) setUserPermissions(JSON.parse(storedPermissions));
    } catch {
      localStorage.removeItem("userPermissions");
    }

    if (!idUsuario) return;

    const apiUrl = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL;
    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
      id_user: idUsuario,
    };

    const verificarAsociacionAleluya = async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/aleluya/asociacion`, { headers });
        const tieneAsociacion = response.data?.tieneIdAleluya === true;
        setTieneIdAleluya(tieneAsociacion);

        if (!tieneAsociacion) {
          setUserPicture("");
          localStorage.removeItem("userPicture");
          return;
        }

        try {
          const respuestaPerfil = await axios.get(`${apiUrl}/api/aleluya/mi-perfil`, { headers });
          const foto = respuestaPerfil.data?.foto || "";

          setUserPicture(foto);
          if (foto) {
            localStorage.setItem("userPicture", foto);
          } else {
            localStorage.removeItem("userPicture");
          }
        } catch (errorPerfil) {
          console.warn("No se pudo cargar la foto de Aleluya:", errorPerfil.message);
        }
      } catch (error) {
        // No se restringe el acceso por un error temporal de red; solo cuando
        // el backend confirma que el campo id_aleluya no existe.
        setTieneIdAleluya(null);
        console.warn("No se pudo verificar la asociación con Aleluya:", error.message);
      }
    };

    verificarAsociacionAleluya();
  }, []);

  // Cerrar sidebar al hacer clic fuera
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        isMenuOpen &&
        !sidebarRef.current.contains(event.target) &&
        !menuButtonRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
        setHoveredModule(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setHoveredModule(null);
  };

  const handleLogout = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/logout`,
        {id: localStorage.getItem('userId')},
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("userRole");
      localStorage.removeItem("userName");
      localStorage.removeItem("userPicture");
      localStorage.removeItem("userPermissions");
      window.location.href = "/login";
    }
  };

  //Función para manejar clic en enlace externo
  const handleExternalLink = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Verificar permisos para un módulo
  const checkPermission = (moduleName, submenuItem = null) => {
    if (!userRole) return false;
    const permissionKey = getPermissionKey(moduleName, submenuItem);
    if (userPermissions && Object.prototype.hasOwnProperty.call(userPermissions, permissionKey)) {
      return userPermissions[permissionKey] === true;
    }
    if (userRole === ROLES.ADMIN) return true;

    switch (moduleName) {
      case "clientes":
        return userRole === ROLES.ADMIN || userRole === ROLES.SALES;
      case "cotizaciones":
        return userRole === ROLES.SALES;
      case "remisiones":
        return userRole === ROLES.SALES;
      case "pagos":
        if (submenuItem === "VERIFICAR") {
          return userRole === ROLES.FINANCIAL || userRole === ROLES.SALES;
        }
        return userRole === ROLES.FINANCIAL || userRole === ROLES.SALES;
      case "bodega":
        return userRole === ROLES.WAREHOUSE || userRole === ROLES.SALES || userRole === ROLES.AUXILIAR;
      case "garantias":
        return userRole === ROLES.ADMIN || userRole === ROLES.SALES;
      case "facturacion":
        return userRole === ROLES.FINANCIAL;
      case "puntos":
        return userRole === ROLES.ADMIN || userRole === ROLES.SALES;
      case "reportes":
        return Object.values(ROLES).includes(userRole);
      case "permisos":
        return userRole === ROLES.ADMIN;
      default:
        return false;
    }
  };

  const getPermissionKey = (moduleName, submenuItem) => {
    const submenuKeys = {
      "CREAR REMISIÓN": "remisiones.crear",
      "CONSULTAR": moduleName === "remisiones" ? "remisiones.consultar" : "garantias.consultar",
      "ANULAR": "remisiones.anular",
      "REGISTRAR PAGO": "pagos.registrar",
      "VERIFICAR": "pagos.verificar",
      "HISTORIAL": "pagos.verificar",
      "EMITIR / EMITIDAS": "facturacion.emitir",
      "FACTURAS DE CONTADO SIN PAGO": "facturacion.contado",
      "ALISTAMIENTOS": "bodega.alistamientos",
      "EN CAMINO": "bodega.ordenes_compra",
      "TRASLADOS DE MERCANCÍA": "bodega.traslados",
      "TRASLADOS DE PEDIDOS": "bodega.traslados_pedidos",
      "SERIALIZAR": "bodega.serializar",
      "CONSULTAR SERIAL": "bodega.consultar_serial",
      "REGISTRAR": "garantias.registrar",
    };
    return submenuItem ? (submenuKeys[submenuItem] || moduleName) : moduleName;
  };

  const modules = [
  {
    name: 'reportes',
    icon: <AssignmentIcon className="menuIcon" />,
    label: 'REPORTES',
    link: '/reportes',
    submenu: [],
  },
    {
      name: "clientes",
      icon: <PeopleIcon className="menuIcon" />,
      label: "CLIENTES",
      link: "/clientes",
      submenu: [],
    },
    {
      name: "cotizaciones",
      icon: <AssignmentIcon className="menuIcon" />,
      label: "COTIZACIONES",
      link: "/cotizaciones",
      submenu: [],
    },
    {
      name: "remisiones",
      icon: <InventoryIcon className="menuIcon" />,
      label: "REMISIONES",
      link: "/remisiones",
      submenu: [
        { label: "CREAR REMISIÓN", link: "/remisiones/crear-remision" },
        { label: "CONSULTAR", link: "/remisiones/consultar-remision" },
        { label: "ANULAR", link: "/remisiones/anular" },
      ],
    },
    {
      name: "pagos",
      icon: <PaymentIcon className="menuIcon" />,
      label: "PAGOS",
      submenu: [
        { label: "REGISTRAR PAGO", link: "/pagos/ingresar-pago" },
        { 
          label: userRole === ROLES.SALES ? "HISTORIAL" : "VERIFICAR", 
          link: "/pagos/verificar" 
        },
      ],
    },
    {
      name: "facturacion",
      icon: <DescriptionIcon className="menuIcon" />,
      label: "FACTURACIÓN",
      submenu: [
        { label: "EMITIR / EMITIDAS", link: "/facturacion" },
        { 
          label: "FACTURAS DE CONTADO SIN PAGO", 
          link: "/facturas-contado-sin-pago" 
        },
      ],
    },
    {
      name: "bodega",
      icon: <StoreIcon className="menuIcon" />,
      label: "BODEGA",
      submenu: [
        { label: "ALISTAMIENTOS", link: "/bodega/alistamientos" },
        { label: "EN CAMINO", link: "/bodega/ordenes-compra" },
        { label: "TRASLADOS DE MERCANCÍA", link: "/bodega/traslados" },
        { label: "TRASLADOS DE PEDIDOS", link: "/bodega/traslados-pedidos" },
        { label: "SERIALIZAR", link: "/bodega/inventario" },
        { label: "CONSULTAR SERIAL", link: "/bodega/seriales" },
      ],
    },
    {
      name: "garantias",
      icon: <VerifiedIcon className="menuIcon" />,
      label: "GARANTÍAS",
      submenu: [
        { 
          label: "REGISTRAR", 
          isExternal: true,
          externalUrl: "http://50.28.103.119:8082/open.php"
        },
        { 
          label: "CONSULTAR", 
          isExternal: true,
          externalUrl: "http://50.28.103.119:8082/scp/login.php"
        },
      ],
    },
    {
      name: "puntos",
      label: "PUNTOS",
      icon: <CardGiftcardIcon className="menuIcon" />,
      link: "/redimir-puntos",
      submenu: [],
    },
    {
      name: "permisos",
      icon: <AdminPanelSettingsIcon className="menuIcon" />,
      label: "PERMISOS",
      link: "/permisos",
      submenu: [],
    },
  ];

  const isCurrentModule = (module) =>
    module.link === location.pathname ||
    (module.name === "reportes" && location.pathname.startsWith("/reportes/")) ||
    module.submenu.some((item) => item.link === location.pathname);

  const isReadableForm = ["/cotizaciones", "/remisiones", "/remisiones/crear-remision"]
    .includes(location.pathname);

  const accesoAleluyaRestringido = tieneIdAleluya === false;

  const RestrictedItem = ({ children, moduleName, submenuItem = null }) => {
    const hasPermission = checkPermission(moduleName, submenuItem);

    return hasPermission ? (
      children
    ) : (
      <Tooltip title="No tienes permisos para acceder a este módulo" arrow>
        <div className="restricted-item">
          {children}
          <div className="restricted-overlay"></div>
        </div>
      </Tooltip>
    );
  };

  return (
    <div className={`layout ${isMenuOpen ? "menu-open" : ""}`}>
      {/* ISLA FLOTANTE - Estilo Dynamic Island */}
      <div className="dynamic-island">
        <Link to="/home" className="island-logo-link" onClick={() => setIsMenuOpen(false)}>
          <img src={logo} alt="Logo" className="island-logo" />
        </Link>
        
        <button
          ref={menuButtonRef}
          onClick={toggleMenu}
          className={`island-menu-button ${isMenuOpen ? "open" : ""}`}
          aria-label={`${isMenuOpen ? "Cerrar menú" : "Abrir menú"}${reportesNoLeidas > 0 ? `, ${reportesNoLeidas} notificaciones de reportes sin leer` : ""}`}
          aria-expanded={isMenuOpen}
          style={{ position: "relative" }}
        >
          {isMenuOpen ? <CloseRoundedIcon /> : <MenuRoundedIcon />}
          {reportesNoLeidas > 0 && (
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                top: -5,
                right: -5,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 18,
                height: 18,
                boxSizing: "border-box",
                padding: "0 4px",
                border: "2px solid #fff",
                borderRadius: 999,
                backgroundColor: "#dc2626",
                color: "#fff",
                fontSize: 10,
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              {reportesNoLeidas > 99 ? "99+" : reportesNoLeidas}
            </span>
          )}
        </button>
      </div>

      <div ref={sidebarRef}>
        <nav className={`sidebar ${isMenuOpen ? "open" : "closed"}`}>
          {/* SECCIÓN DEL USUARIO - CON DATOS DINÁMICOS */}
          <Tooltip
            title={accesoAleluyaRestringido ? "Solicita asociar tu cuenta de aleluya para ver tus datos" : ""}
            arrow
          >
          <div>
          <Link
            to={accesoAleluyaRestringido ? "#" : "/mi-perfil"}
            className={`user-section ${accesoAleluyaRestringido ? "user-section-disabled" : ""}`}
            onClick={(event) => {
              if (accesoAleluyaRestringido) {
                event.preventDefault();
                return;
              }
              setIsMenuOpen(false);
              setHoveredModule(null);
            }}
            aria-label={accesoAleluyaRestringido ? "Módulo de Aleluya no disponible" : "Ver mi perfil"}
            aria-disabled={accesoAleluyaRestringido}
          >
            <div className="user-avatar">
              {userPicture ? (
                <Avatar
                  src={userPicture}
                  alt={`Foto de ${userName}`}
                  className="user-profile-picture"
                  onError={() => {
                    setUserPicture("");
                    localStorage.removeItem("userPicture");
                  }}
                />
              ) : (
                userRole && ROLE_ICONS[userRole]
              )}
            </div>
            <div className="user-info">
              <div className="user-name">{userName}</div>
              <div className="user-role">
                {userRole && ROLE_LABELS[userRole]}
              </div>
            </div>
          </Link>
          </div>
          </Tooltip>

          <ul className="menu">
            <li className="menu-label">Navegación</li>
            {modules.map((module) => (
              <li
                key={module.name}
                className={`menu-item-container ${
                  hoveredModule === module.name ? "active" : ""
                } ${isCurrentModule(module) ? "is-current" : ""}`}
                onClick={() =>
                  module.submenu.length > 0 &&
                  setHoveredModule(
                    hoveredModule === module.name ? null : module.name
                  )
                }
              >
                <RestrictedItem moduleName={module.name}>
                  {module.submenu.length > 0 ? (
                    <div className="menuItem">
                      <div className="menuItemContent">
                        {module.icon}
                        <span>{module.label}</span>
                      </div>
                      <ExpandMoreRoundedIcon className="expand-icon" />
                    </div>
                  ) : module.isExternal ? (
                    <div
                      className="menuItem"
                      onClick={(e) => {
                        if (!checkPermission(module.name)) {
                          e.preventDefault();
                          return;
                        }
                        const email = localStorage.getItem('userEmail');
                        window.location.href = `${module.link}?email=${email}`;
                      }}
                    >
                      <div className="menuItemContent">
                        {module.icon}
                        <span>{module.label}</span>
                      </div>
                    </div>
                  ) : module.link ? (
                    <Link
                      to={checkPermission(module.name) ? module.link : "#"}
                      className="menuItem"
                      onClick={(e) =>
                        !checkPermission(module.name) && e.preventDefault()
                      }
                    >
                      <div className="menuItemContent">
                        {module.icon}
                        <span>{module.label}</span>
                      </div>
                    </Link>
                  ) : null}
                </RestrictedItem>

                {module.submenu.length > 0 &&
                  hoveredModule === module.name && (
                    <ul className="submenu-items">
                      {module.submenu.map((item, idx) => (
                        <li key={idx}>
                          <RestrictedItem
                            moduleName={module.name}
                            submenuItem={item.label}
                          >
                            {item.isExternal ? (
                              <div
                                className="submenuItem"
                                onClick={() => handleExternalLink(item.externalUrl)}
                              >
                                {item.label}
                              </div>
                            ) : (
                              <Link
                                to={
                                  checkPermission(module.name, item.label)
                                    ? item.link
                                    : "#"
                                }
                                className="submenuItem"
                                onClick={(e) =>
                                  !checkPermission(module.name, item.label) &&
                                  e.preventDefault()
                                }
                              >
                                {item.label}
                              </Link>
                            )}
                          </RestrictedItem>
                        </li>
                      ))}
                    </ul>
                  )}
              </li>
            ))}
          </ul>

          <div className="theme-toggle">
            <div className="theme-toggle-label">
              {isDarkMode ? <DarkModeOutlinedIcon className="menuIcon" /> : <LightModeOutlinedIcon className="menuIcon" />}
              <span>MODO OSCURO</span>
            </div>
            <Switch
              checked={isDarkMode}
              onChange={handleThemeChange}
              inputProps={{ "aria-label": "Activar modo oscuro" }}
              size="small"
            />
          </div>

          <div className="logout">
            <button onClick={handleLogout} className="menuItem">
              <LogoutIcon className="menuIcon" />
              <span>CERRAR SESIÓN</span>
            </button>
          </div>
        </nav>
      </div>

      <NotificacionesReportes onCountChange={setReportesNoLeidas} />
      <main className="mainContent">
        <div className={`content-container ${isReadableForm ? "readable-form" : ""}`}>{children}</div>
      </main>
    </div>
  );
}

export default Sidebar;